import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowUpRight, CalendarClock, Check, ChevronRight, Circle, Loader2, Reply, Settings, Trash2,
} from 'lucide-react';
import { api } from '../../../services/api';
import GmailConnectPanel, { MailUnavailable } from './GmailConnectPanel';
import MailDetailSheet from './MailDetailSheet';
import MailDigestSettingsSheet from './MailDigestSettingsSheet';
import { useNow, useSyncProgress } from './mailHooks';
import { C, S, btn, clampLines } from './mailStyles';
import {
  DIGEST_SECTIONS, OTHER_BUCKETS, absoluteTime, capCount, claimConnectedFollowUp, emailsLabel,
  mailErrorKind, monogram, monogramColor, parseSender, providerName, providerOf, relativeTime,
} from './mailUtils';

const PREVIEW_ROWS = 3;
const EXPANDED_ROW_CAP = 200;
const STATUS_CHUNK = 100;
const SCAN_STEPS = ['Sync new mail', 'Sort & prioritize', 'Write summary'];
const NO_ROWS = [];

async function fetchStatuses(ids) {
  const chunks = [];
  for (let i = 0; i < ids.length; i += STATUS_CHUNK) chunks.push(ids.slice(i, i + STATUS_CHUNK));
  const results = await Promise.all(chunks.map((c) => api.getMailItemsStatus(c).catch(() => null)));
  const merged = {};
  for (const r of results) {
    if (r?.items && typeof r.items === 'object') Object.assign(merged, r.items);
  }
  return merged;
}

function chunkIds(ids) {
  const chunks = [];
  for (let i = 0; i < ids.length; i += STATUS_CHUNK) chunks.push(ids.slice(i, i + STATUS_CHUNK));
  return chunks;
}

// state: loading | ok | disconnected | disabled | error
async function fetchBatchRows(ids, signal) {
  const results = await Promise.all(chunkIds(ids).map((c) => api.getMailItemsBatch(c, { signal })));
  const byId = {};
  let connected = true;
  for (const r of results) {
    if (r?.connected === false) connected = false;
    if (r?.items && typeof r.items === 'object') Object.assign(byId, r.items);
  }
  if (!connected) return { state: 'disconnected', items: NO_ROWS };
  const items = ids
    .map((id) => {
      const it = byId[id];
      return it && typeof it === 'object' ? { ...it, message_id: typeof it.message_id === 'string' ? it.message_id : id } : null;
    })
    .filter(Boolean);
  return { state: 'ok', items };
}

function useBatchRows(itemIds, active) {
  const idsKey = active ? itemIds.join('\n') : '';
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState({ key: null, state: 'loading', items: NO_ROWS });
  const key = idsKey ? `${idsKey}|${reloadKey}` : null;

  useEffect(() => {
    if (!key) return undefined;
    const controller = new AbortController();
    fetchBatchRows(idsKey.split('\n'), controller.signal)
      .then((r) => { if (!controller.signal.aborted) setResult({ key, ...r }); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        const kind = mailErrorKind(err);
        setResult({ key, state: kind === 'auth' ? 'disconnected' : kind === 'disabled' ? 'disabled' : 'error', items: NO_ROWS });
      });
    return () => controller.abort();
  }, [key, idsKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  if (!key) return { state: 'ok', items: NO_ROWS, reload };
  if (result.key !== key) return { state: 'loading', items: NO_ROWS, reload };
  return { ...result, reload };
}

function StepIcon({ state }) {
  if (state === 'active') return <Loader2 size={16} className="animate-spin" color={C.accent} aria-hidden="true" />;
  if (state === 'done') return <Check size={16} color={C.green} strokeWidth={2.5} aria-hidden="true" />;
  return <Circle size={16} color={C.border} aria-hidden="true" />;
}

function progressNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function scanSteps(progress) {
  const stage = progress?.state === 'running' ? progress.stage : null;
  const fetched = progressNumber(progress?.fetched);
  const total = progressNumber(progress?.total) || fetched;
  const sorted = Math.min(progressNumber(progress?.sorted), total || Infinity);
  const fetchedDone = { state: 'done', label: 'Fetched', count: total, suffix: total === 1 ? 'email' : 'emails' };
  if (stage === 'fetching') {
    return [
      { state: 'active', label: 'Fetched', count: fetched, suffix: fetched === 1 ? 'email' : 'emails', plainCount: true },
      { state: 'pending', label: SCAN_STEPS[1] },
      { state: 'pending', label: SCAN_STEPS[2] },
    ];
  }
  if (stage === 'sorting') {
    return [
      fetchedDone,
      { state: 'active', label: SCAN_STEPS[1], sorted, total },
      { state: 'pending', label: SCAN_STEPS[2] },
    ];
  }
  if (stage === 'summarizing') {
    return [fetchedDone, { state: 'done', label: SCAN_STEPS[1] }, { state: 'active', label: SCAN_STEPS[2] }];
  }
  return SCAN_STEPS.map((label, i) => ({ state: i === 0 ? 'active' : 'pending', label }));
}

function ScanningCard() {
  const progress = useSyncProgress(true);
  const steps = scanSteps(progress);
  return (
    <div style={S.card} data-testid="mail-digest-scanning">
      <p style={S.title}>Checking your inbox</p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {steps.map((step, i) => (
          <li
            key={i}
            data-testid="mail-scan-step"
            data-state={step.state}
            style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, color: step.state === 'pending' ? C.textFaint : C.text }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <StepIcon state={step.state} />
              <span style={{ fontWeight: step.state === 'active' ? 600 : 400, display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                {step.suffix ? (
                  <>
                    {step.label}{' '}
                    {step.plainCount
                      ? step.count.toLocaleString()
                      : <span style={S.chip}>{step.count.toLocaleString()}</span>}
                    {' '}{step.suffix}
                  </>
                ) : step.label}
              </span>
              {step.total > 0 && (
                <span style={{ marginLeft: 'auto', fontSize: 12.5, color: C.textDim, flexShrink: 0 }} data-testid="mail-scan-sorted">
                  {step.sorted.toLocaleString()} / {step.total.toLocaleString()}
                </span>
              )}
            </div>
            {step.total > 0 && (
              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={step.total}
                aria-valuenow={step.sorted}
                style={{ height: 4, borderRadius: 2, background: C.bgMuted, marginLeft: 26, overflow: 'hidden' }}
              >
                <div style={{ height: '100%', width: `${Math.round((step.sorted / step.total) * 100)}%`, background: C.accent, transition: 'width 0.3s' }} />
              </div>
            )}
          </li>
        ))}
      </ul>
      <p style={S.sub}>Feel free to leave — this keeps running</p>
    </div>
  );
}

function DigestLoading() {
  return (
    <div style={S.card} data-testid="mail-digest-loading">
      {['55%', '90%', '75%', '85%'].map((w, i) => (
        <div key={i} style={{ width: w, height: i === 0 ? 16 : 12, borderRadius: 6, background: C.bgMuted }} />
      ))}
    </div>
  );
}

function EmailRow({ item, now, onOpen }) {
  const sender = parseSender(item.from);
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid="mail-email-row"
      style={{
        display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '10px 0', border: 'none',
        borderTop: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', fontFamily: C.font, color: C.text,
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 16, background: monogramColor(sender.name), color: '#fff', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
      }}>
        {monogram(sender.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
          <span style={{ ...S.ellipsis, flex: 1, fontSize: 13, color: C.textDim }}>{sender.name}</span>
          <span style={{ fontSize: 11.5, color: C.textFaint, flexShrink: 0 }}>{relativeTime(item.received_at, now)}</span>
        </div>
        <span style={{ ...S.ellipsis, fontSize: 14, fontWeight: 700 }}>{item.subject || '(no subject)'}</span>
        {item.summary && (
          <span style={{ fontSize: 13, color: C.textDim, lineHeight: 1.4, ...clampLines(2) }}>{item.summary}</span>
        )}
        {item.suggested_action && (
          <span style={{ ...S.chip, alignSelf: 'flex-start', maxWidth: '100%', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.suggested_action}
          </span>
        )}
      </div>
    </button>
  );
}

function DigestSection({ label, rows, now, expanded, onToggle, onOpen }) {
  const shown = expanded ? rows.slice(0, EXPANDED_ROW_CAP) : rows.slice(0, PREVIEW_ROWS);
  return (
    <section style={{ display: 'flex', flexDirection: 'column' }} data-testid="mail-digest-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span>
        <span style={S.chip}>{capCount(rows.length)}</span>
      </div>
      <div style={expanded ? { maxHeight: 360, overflowY: 'auto' } : undefined}>
        {shown.map((item) => <EmailRow key={item.message_id} item={item} now={now} onOpen={onOpen} />)}
        {expanded && rows.length > EXPANDED_ROW_CAP && (
          <p style={{ ...S.sub, padding: '8px 0' }}>Showing the first {EXPANDED_ROW_CAP} of {rows.length}</p>
        )}
      </div>
      {rows.length > PREVIEW_ROWS && (
        <button type="button" style={{ ...S.btnLink, alignSelf: 'flex-start', paddingTop: 6 }} onClick={onToggle}>
          {expanded ? 'Show less' : `See all ${capCount(rows.length)}`}
        </button>
      )}
    </section>
  );
}

function EverythingElse({ byBucket }) {
  const parts = OTHER_BUCKETS
    .map(({ bucket, singular, plural }) => {
      const n = Number(byBucket?.[bucket]) || 0;
      return n > 0 ? `${n} ${n === 1 ? singular : plural}` : null;
    })
    .filter(Boolean);
  const total = OTHER_BUCKETS.reduce((sum, { bucket }) => sum + (Number(byBucket?.[bucket]) || 0), 0);
  if (total === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: C.bgMuted, borderRadius: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Everything else</div>
        <div style={{ ...S.ellipsis, fontSize: 12, color: C.textDim }}>{parts.join(' · ')}</div>
      </div>
      <span style={{ ...S.chip, background: C.bg }}>{capCount(total)}</span>
    </div>
  );
}

function NextStepRow({ icon: Icon, text, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid="mail-next-step"
      style={btn({
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', borderRadius: 10,
        border: `1px solid ${C.border}`, background: C.bg, cursor: 'pointer', fontFamily: C.font, color: C.text, textAlign: 'left',
      }, disabled)}
    >
      <Icon size={16} color={C.accent} style={{ flexShrink: 0 }} aria-hidden="true" />
      <span style={{ ...S.ellipsis, flex: 1, fontSize: 13.5, fontWeight: 600 }}>{text}</span>
      <ArrowUpRight size={15} color={C.textFaint} style={{ flexShrink: 0 }} aria-hidden="true" />
    </button>
  );
}

export default function MailDigestCard({ cardId, data, sendFollowUp }) {
  const now = useNow(60000);
  const [statusMap, setStatusMap] = useState({});
  const [removed, setRemoved] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [openItem, setOpenItem] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(null);
  const [retryTapped, setRetryTapped] = useState(false);
  const [cleanupTapped, setCleanupTapped] = useState(false);

  const status = data?.status;
  const authRequired = status === 'auth_required' || data?.code === 'auth_required';
  const scanning = !authRequired && (status === 'scanning' || !status);
  const rawItems = data?.items;
  const isV1 = Array.isArray(rawItems);
  const rawItemIds = data?.item_ids;
  const itemIds = useMemo(
    () => (!isV1 && Array.isArray(rawItemIds) ? [...new Set(rawItemIds.filter((id) => typeof id === 'string' && id))] : []),
    [isV1, rawItemIds],
  );
  const batch = useBatchRows(itemIds, !authRequired && !scanning);
  const batchItems = batch.items;
  const items = useMemo(() => {
    const src = isV1 ? rawItems : batchItems;
    return src.filter((it) => it && typeof it.message_id === 'string');
  }, [isV1, rawItems, batchItems]);
  const idsKey = useMemo(() => [...new Set(items.map((it) => it.message_id))].join('\n'), [items]);

  const refreshStatuses = useCallback(async () => {
    if (!idsKey) return;
    const merged = await fetchStatuses(idsKey.split('\n'));
    setStatusMap((prev) => ({ ...prev, ...merged }));
  }, [idsKey]);

  useEffect(() => {
    if (!idsKey || !isV1) return undefined;
    let alive = true;
    fetchStatuses(idsKey.split('\n')).then((merged) => {
      if (alive) setStatusMap((prev) => ({ ...prev, ...merged }));
    });
    return () => { alive = false; };
  }, [idsKey, isV1]);

  const visible = useMemo(() => items.filter((it) => {
    const st = statusMap[it.message_id] ?? it.status;
    return !removed.has(it.message_id) && st !== 'done' && st !== 'dismissed';
  }), [items, statusMap, removed]);

  const sections = useMemo(() => DIGEST_SECTIONS
    .map((s) => ({ ...s, rows: visible.filter((it) => it.bucket === s.bucket) }))
    .filter((s) => s.rows.length > 0), [visible]);
  const needCount = sections.reduce((n, s) => n + s.rows.length, 0);

  const closeSheet = useCallback(() => {
    setOpenItem(null);
    refreshStatuses();
  }, [refreshStatuses]);

  const onStatusChanged = useCallback((id, status) => {
    if (status === 'done' || status === 'dismissed') setRemoved((prev) => new Set(prev).add(id));
  }, []);

  const onDraftReply = useCallback((item, senderName) => {
    sendFollowUp(`draft-reply-${item.message_id}`, `Draft a reply to ${senderName}`, {
      mail_action: 'draft_reply', message_id: item.message_id, thread_id: item.thread_id || '',
    });
    closeSheet();
  }, [sendFollowUp, closeSheet]);

  const provider = providerOf(data?.provider);
  const onReconnected = useCallback(() => {
    if (claimConnectedFollowUp(`${cardId}:digest`)) {
      sendFollowUp('reconnected', `I've connected ${providerName(provider)}`, { mail_event: 'connected' });
    }
  }, [cardId, provider, sendFollowUp]);

  const toggleSection = (bucket) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(bucket)) next.delete(bucket); else next.add(bucket);
    return next;
  });

  if (authRequired) {
    return <GmailConnectPanel expired provider={provider} onConnected={onReconnected} />;
  }
  if (scanning) return <ScanningCard />;
  if (batch.state === 'disabled') return <MailUnavailable />;
  if (batch.state === 'disconnected') return <GmailConnectPanel disconnected provider={provider} onConnected={batch.reload} />;
  if (batch.state === 'loading') return <DigestLoading />;

  const failed = status === 'error' || data?.refresh === 'failed';
  const digest = data?.digest && typeof data.digest === 'object' ? data.digest : null;
  const newTotal = Number(digest?.new_total);
  const enabled = digestEnabled ?? (typeof data?.digest_enabled === 'boolean' ? data.digest_enabled : null);
  const updated = relativeTime(data?.last_synced_at, now);
  const subline = [
    Number.isFinite(newTotal) ? `${capCount(newTotal)} new today` : null,
    updated ? `Updated ${updated}` : null,
  ].filter(Boolean).join(' · ');
  const firstNeedsReply = sections.find((s) => s.bucket === 'needs_reply')?.rows[0] || null;
  const firstNeedsReplySender = parseSender(firstNeedsReply?.from);
  const batchFailed = batch.state === 'error';
  const showEmpty = needCount === 0 && !batchFailed && (status === 'ok' || digest);
  const cleanupCount = (Number(digest?.new_by_bucket?.promo) || 0) + (Number(digest?.new_by_bucket?.newsletter) || 0);
  const nextReply = needCount > 0 ? firstNeedsReply : null;
  const nextDigest = needCount > 0 && enabled !== true;
  const nextCleanup = (needCount > 0 || showEmpty) && cleanupCount > 0;

  return (
    <div style={S.card} data-testid="mail-digest-card">
      {failed && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: '#FFF4E5', borderRadius: 10, padding: '10px 12px' }}>
          <AlertTriangle size={16} color={C.orange} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>Sync didn't finish</div>
            {data?.last_synced_at && (
              <div style={{ fontSize: 12.5, color: C.textDim }}>Showing results from {absoluteTime(data.last_synced_at)}</div>
            )}
          </div>
          <button
            type="button"
            style={btn({ ...S.btnLink, flexShrink: 0 }, retryTapped)}
            disabled={retryTapped}
            onClick={() => {
              setRetryTapped(true);
              sendFollowUp('retry', 'Refresh my email digest', { mail_action: 'refresh' });
            }}
          >
            Retry
          </button>
        </div>
      )}

      {batchFailed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }} data-testid="mail-digest-batch-error">
          <p style={S.error}>Couldn't load these emails.</p>
          <button type="button" style={S.btnLink} onClick={batch.reload}>Retry</button>
        </div>
      )}

      {needCount > 0 && (
        <>
          <div>
            <p style={S.title} data-testid="mail-digest-headline">
              {capCount(needCount)} {needCount === 1 ? 'email needs' : 'emails need'} you
            </p>
            {subline && <p style={S.sub}>{subline}</p>}
          </div>
          {sections.map((s) => (
            <DigestSection
              key={s.bucket}
              label={s.label}
              rows={s.rows}
              now={now}
              expanded={expanded.has(s.bucket)}
              onToggle={() => toggleSection(s.bucket)}
              onOpen={setOpenItem}
            />
          ))}
        </>
      )}

      {showEmpty && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} data-testid="mail-digest-empty">
          <p style={S.title}>Nothing needs you today 🎉</p>
          <p style={S.sub}>
            {Number.isFinite(newTotal) ? `${emailsLabel(newTotal)} new` : 'New emails'} — all updates, newsletters, already sorted.
          </p>
        </div>
      )}

      {(needCount > 0 || showEmpty) && <EverythingElse byBucket={digest?.new_by_bucket} />}
      {(nextReply || nextDigest || nextCleanup) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="mail-next-steps">
          <span style={S.label}>Next steps</span>
          {nextReply && (
            <NextStepRow
              icon={Reply}
              text={`Draft a reply to ${firstNeedsReplySender.name.split(/\s+/)[0]}`}
              onClick={() => onDraftReply(firstNeedsReply, firstNeedsReplySender.name)}
            />
          )}
          {nextCleanup && (
            <NextStepRow
              icon={Trash2}
              text={`Clean up promotions (${capCount(cleanupCount)})`}
              disabled={cleanupTapped}
              onClick={() => {
                if (cleanupTapped) return;
                setCleanupTapped(true);
                sendFollowUp('cleanup-promotions', 'Unsubscribe me from promo emails', { mail_action: 'unsubscribe_scan' });
              }}
            />
          )}
          {nextDigest && (
            <NextStepRow icon={CalendarClock} text="Send me a digest every day at 8 AM" onClick={() => setSettingsOpen(true)} />
          )}
        </div>
      )}
      {digest?.truncated && <p style={S.footnote}>Showing the most recent emails only</p>}

      {(needCount > 0 || showEmpty) && (
        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 0 0', border: 'none',
            borderTop: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', fontFamily: C.font, color: C.text,
          }}
        >
          <Settings size={15} color={C.textDim} aria-hidden="true" />
          <span style={{ flex: 1, textAlign: 'left', fontSize: 13.5, fontWeight: 600 }}>Daily digest</span>
          {enabled != null && <span style={{ fontSize: 12.5, color: C.textDim }}>{enabled ? 'On' : 'Off'}</span>}
          <ChevronRight size={15} color={C.textFaint} aria-hidden="true" />
        </button>
      )}

      {openItem && (
        <MailDetailSheet
          item={openItem}
          onClose={closeSheet}
          onDraftReply={onDraftReply}
          onStatusChanged={onStatusChanged}
        />
      )}
      {settingsOpen && (
        <MailDigestSettingsSheet
          onClose={() => setSettingsOpen(false)}
          onSaved={(s) => setDigestEnabled(Boolean(s.enabled))}
        />
      )}
    </div>
  );
}
